/** @jsx jsx */
import { jsx } from 'theme-ui';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Center,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  useToast,
} from '@chakra-ui/core';
import _isString from 'lodash/isString';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useStoreActions, useStoreState } from '../../../../../models/hooks';
import { useSeekerContainerContext } from '../../SeekerContainerContext';
import { confirmationModalHeaderCSS } from './ConfirmationModal.styles';
import { useInterviewConfig } from './useInterviewConfig';
import { AppointmentType } from '../../../../../firebase/firestore/documents/position';
import { AppointmentTypeIcon } from '../../../../common/AppointmentTypeIcon/AppointmentTypeIcon';

export type MeetConfirmationModalProps = {
  isOpen: boolean;
  disabled: boolean;
  onClose: () => void;
  isMessagingView: boolean;
};

export const MeetConfirmationModal = ({
  isOpen,
  disabled,
  onClose,
  isMessagingView,
}: MeetConfirmationModalProps): JSX.Element => {
  const history = useHistory();
  const { t } = useTranslation();
  const toast = useToast();
  const { selectedCandidate } = useSeekerContainerContext();
  const selectedCandidatePositionID = selectedCandidate?.position;
  const interviewConfig = useInterviewConfig();

  const createAppointmentRequest = useStoreActions((actions) => actions.messaging.createAppointmentRequest);
  const loadAppointmentAvailabilities = useStoreActions((actions) => actions.messaging.loadAppointmentAvailabilities);
  const availabilitiesCount = useStoreState((state) => state.messaging.availabilitiesCount);
  const isCalendarEnabled = useStoreState((s) => s.app.accounts?.calendarEnabled);
  const recruiterId = useStoreState((state) => state.auth.firebaseUser?.uid);
  const isWatchedRecruiter = selectedCandidate?.watchedRecruiters?.includes(recruiterId ?? '');

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isFetchingAvailabilities, setIsFetchingAvailabilities] = useState<boolean>(false);

  useEffect(() => {
    if (selectedCandidatePositionID) {
      setIsFetchingAvailabilities(true);
      // eslint-disable-next-line no-void, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
      void loadAppointmentAvailabilities({ positionId: selectedCandidatePositionID }).finally(() => {
        setIsFetchingAvailabilities(false);
      });
    }
  }, [loadAppointmentAvailabilities, selectedCandidatePositionID]);

  // function to create appointment request
  const handleCreateAppointmentRequest = async () => {
    if (!selectedCandidate) {
      return;
    }
    try {
      setIsLoading(true);
      await createAppointmentRequest({
        positionId: selectedCandidate.position,
        seekerId: selectedCandidate.seeker,
      }).then((data) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (!_isString(data) && data?.code === 'interview.exists') {
          toast({
            title: t('messaging:actions.adHoc.confirmationModal.interviewExist'),
            status: 'error',
            duration: 3000,
            isClosable: true,
          });
        }

        onClose();
        setIsLoading(false);
      });
    } finally {
      onClose();
      setIsLoading(false);
    }
  };

  const getAppointmentTypeText = (appointmentType: AppointmentType): string => t(`common:appointmentTypes.${appointmentType}`);

  // istanbul ignore next
  const getHeaderText = (): string => {
    if (interviewConfig) {
      return t('messaging:actions.meet.confirmationModal.header', {
        duration: interviewConfig.getDuration(),
        type: getAppointmentTypeText(interviewConfig.getType()),
      });
    }
    return '';
  };

  const getHeader = (text: string) => (
    <ModalHeader css={confirmationModalHeaderCSS} className="modal-header">
      <Flex direction="row" alignItems="center">
        <AppointmentTypeIcon type={interviewConfig?.getType()} />
        <Box width={2} />
        {text}
      </Flex>
    </ModalHeader>
  );

  const renderLoadingContent = () => (
    <ModalContent>
      <ModalBody alignItems="center" justifyContent="center">
        <Center>
          <Spinner data-testid="spinner" size="xl" />
        </Center>
      </ModalBody>
    </ModalContent>
  );

  const renderConfirmationContent = () => (
    <ModalContent>
      {getHeader(getHeaderText())}
      <ModalBody>{t('messaging:actions.meet.confirmationModal.body')}</ModalBody>
      <ModalFooter>
        <Button data-testid="CancelButton" colorScheme="red" mr={3} onClick={onClose} disabled={isLoading}>
          {t('messaging:actions.meet.confirmationModal.cancelButton')}
        </Button>
        <Button
          data-testid="SendButton"
          colorScheme="blue"
          onClick={handleCreateAppointmentRequest}
          disabled={disabled}
          isLoading={isLoading}
        >
          {t('messaging:actions.meet.confirmationModal.sendButton')}
        </Button>
      </ModalFooter>
    </ModalContent>
  );

  const renderCalendarDisabledContent = () => (
    <ModalContent>
      {getHeader(t('messaging:actions.meet.notificationModal.header'))}
      <ModalBody>{t('messaging:actions.meet.notificationModal.addAvailabilityThroughMobileApp')}</ModalBody>
      <ModalFooter>
        <Button data-testid="okBtn" colorScheme="blue" onClick={onClose}>
          {t('common:button.okay')}
        </Button>
      </ModalFooter>
    </ModalContent>
  );

  const renderNoAvailabilityContent = () => (
    <ModalContent>
      {getHeader(t('messaging:actions.meet.notificationModal.header'))}
      <ModalBody>{t('messaging:actions.meet.notificationModal.body')}</ModalBody>
      <ModalFooter>
        <Button data-testid="CancelButton" colorScheme="red" mr={3} onClick={onClose} disabled={isLoading}>
          {t('messaging:actions.meet.notificationModal.cancelButton')}
        </Button>
        {isMessagingView && (
          <Button
            data-testid="CreateButton"
            colorScheme="blue"
            disabled={disabled}
            isLoading={isLoading}
            onClick={() => history.push({ pathname: '/calendar' }, { state: true })}
          >
            {t('messaging:actions.meet.notificationModal.createButton')}
          </Button>
        )}
      </ModalFooter>
    </ModalContent>
  );

  const renderWatchedRecruiterContent = () => (
    <ModalContent>
      {getHeader(t('messaging:actions.meet.watchedRecruiterModal.header'))}
      <ModalBody>{t('messaging:actions.meet.watchedRecruiterModal.body')}</ModalBody>
      <ModalFooter>
        <Button data-testid="okBtn" colorScheme="blue" onClick={onClose} disabled={isLoading}>
          {t('common:button.okay')}
        </Button>
      </ModalFooter>
    </ModalContent>
  );

  const getContent = () => {
    if (isWatchedRecruiter) return renderWatchedRecruiterContent();
    if (isFetchingAvailabilities) return renderLoadingContent();
    if (availabilitiesCount && availabilitiesCount > 0) return renderConfirmationContent();
    if (!isCalendarEnabled) return renderCalendarDisabledContent();
    return renderNoAvailabilityContent();
  };

  return (
    <Modal id="MeetConfirmationModal" isOpen={isOpen} onClose={onClose} isCentered closeOnOverlayClick={false} size="lg">
      <ModalOverlay>{getContent()}</ModalOverlay>
    </Modal>
  );
};
